import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Calendar } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { createEventoInput } from '@portal-leo/shared';
import type { z } from 'zod';

type CreateEventoInput = z.infer<typeof createEventoInput>;

interface EventoFormPageProps {
  mode: 'create' | 'edit';
}

export function EventoFormPage({ mode }: EventoFormPageProps) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'FINANCEIRO', 'COORDENADOR_EVENTO');

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const utils = trpc.useUtils();

  useEffect(() => {
    if (!canEdit) {
      navigate('/eventos', { replace: true });
    }
  }, [canEdit, navigate]);

  const { data: evento, isLoading: isLoadingEvento } = trpc.eventos.getById.useQuery(
    { id: id! },
    { enabled: mode === 'edit' && !!id }
  );

  const createMutation = trpc.eventos.create.useMutation({
    onSuccess: (data) => {
      utils.eventos.list.invalidate();
      if (data) navigate(`/eventos/${data.id}`);
    },
  });

  const updateMutation = trpc.eventos.update.useMutation({
    onSuccess: () => {
      utils.eventos.list.invalidate();
      utils.eventos.getById.invalidate({ id: id! });
      navigate(`/eventos/${id}`);
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateEventoInput>({
    resolver: zodResolver(createEventoInput),
  });

  useEffect(() => {
    if (mode === 'edit' && evento) {
      reset({
        nome: evento.nome,
        descricao: evento.descricao || '',
        local: evento.local || '',
        valorInscricao: evento.valorInscricao || '',
        limiteParticipantes: evento.limiteParticipantes || undefined,
        dataInicio: evento.dataInicio,
        dataFim: evento.dataFim,
      });
    }
  }, [mode, evento, reset]);

  const onSubmit = (data: CreateEventoInput) => {
    if (mode === 'edit' && id) {
      updateMutation.mutate({
        id,
        ...data,
      });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleCancel = () => {
    if (mode === 'edit' && id) {
      navigate(`/eventos/${id}`);
    } else {
      navigate('/eventos');
    }
  };

  if (mode === 'edit' && isLoadingEvento) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (mode === 'edit' && !evento && !isLoadingEvento) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Evento não encontrado</p>
        <Button variant="outline" onClick={() => navigate('/eventos')} className="mt-4">
          Voltar para eventos
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={handleCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100">
            <Calendar className="h-5 w-5 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {mode === 'create' ? 'Novo Evento' : 'Editar Evento'}
          </h1>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Informações do Evento</h2>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Input
                  label="Nome do Evento"
                  placeholder="Ex: Conferência Anual Leo Clube"
                  {...register('nome')}
                  error={errors.nome?.message}
                />
              </div>

              <div className="md:col-span-2">
                <Input
                  label="Local"
                  placeholder="Ex: Centro de Convenções"
                  {...register('local')}
                  error={errors.local?.message}
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
                <textarea
                  {...register('descricao')}
                  rows={3}
                  placeholder="Descreva o evento..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <Input
                label="Data Início"
                type="date"
                {...register('dataInicio')}
                error={errors.dataInicio?.message}
              />

              <Input
                label="Data Fim"
                type="date"
                {...register('dataFim')}
                error={errors.dataFim?.message}
              />

              <Input
                label="Valor da Inscrição"
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register('valorInscricao')}
                error={errors.valorInscricao?.message}
              />

              <Input
                label="Limite de Participantes"
                type="number"
                placeholder="Deixe vazio para ilimitado"
                {...register('limiteParticipantes', { valueAsNumber: true })}
                error={errors.limiteParticipantes?.message}
              />
            </div>

            {mode === 'edit' && (
              <div className="rounded-lg bg-gray-50 p-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    {...register('ativo' as any)}
                    defaultChecked={evento?.ativo}
                  />
                  <span className="text-sm font-medium text-gray-700">Evento ativo</span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Eventos inativos não aparecem para novas inscrições
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button
                type="submit"
                isLoading={createMutation.isPending || updateMutation.isPending}
              >
                {mode === 'create' ? 'Criar Evento' : 'Salvar Alterações'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default EventoFormPage;
